import { IconName } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { format, formatDistanceToNow } from "date-fns";
import { nb } from "date-fns/locale";
import parse from "html-react-parser";
import {
  createContext,
  Key,
  MouseEvent,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useNavigate } from "react-router-dom";
import AlertBox from "../components/AlertBox";
import BubbleModal from "../components/BubbleModal";
import Button from "../components/Button";
import CustomLink from "../components/CustomLink";
import Footer from "../components/Footer";
import FullscreenLoader from "../components/FullscreenLoader";
import Header from "../components/Header";
import Input from "../components/Input";
import ToggleSwitch from "../components/ToggleSwitch";
import { clearActiveDraft } from "../helpers/activeDraft";
import { getUserSchema } from "../helpers/schema";
import {
  AgreementType,
  ApplicationType,
  FormDataType,
  MessageType,
  ReportItemType,
  UserType,
} from "../helpers/types";
import {
  ApplicationListItemType,
  fetchApplications,
  fetchDeadlines,
  fetchUnreadMessages,
} from "../api/applications";
import { readOidcSub } from "../api/applicationTemplates";
import usePlaygroundAccess from "../hooks/usePlaygroundAccess";
import { getCityFromPostCode, getStatus } from "../helpers/utils";
import {
  formatPeriodText,
  getActiveOrNextPeriod,
} from "../helpers/period-utils";
import { useAuthContext } from "../providers/AuthProvider";
import { useConfigContext } from "../providers/ConfigProvider";
import { AgreementItem } from "./ApplicationAgreements";
import { ProfileAvatar } from "./ApplicationMessages";
import { ReportItem } from "./ApplicationReports";
import { all } from "axios";
import { Skeleton } from 'boneyard-js/react'

type ContextType = {
  active: ApplicationListItemType[] | null;
  archived: ApplicationListItemType[] | null;
  messages: MessageType[] | null;
  drafts: ApplicationListItemType[] | null;
  reports: ReportItemType[] | null;
  agreements: AgreementType[] | null;
  // Minst ett av de tre dashboard-kallene feilet. Uten dette ville en feil
  // (typisk manglende CORS-origin på API-et) etterlatt kolonnene i evig
  // lastetilstand uten å si hvorfor.
  loadFailed: boolean;
  preference: boolean;
  deleteDraft: (draftId: number, applicationType: number) => void;
  handlePreferenceChange: (value: boolean) => void;
};

const Context = createContext<ContextType | null>(null);
const useDashboardContext = () => {
  const context = useContext(Context);
  if (context === null) {
    throw new Error("useDashboardContext must be used within a Provider");
  }

  return context;
};

/**
 * Deler søknadslista i kladder, aktive og arkiverte.
 *
 * Ren funksjon av responsen, så vi slipper tre ekstra state-variabler som må
 * holdes i sync. `null` inn betyr «laster ennå» og gir `null` ut, slik at
 * kortene kan vise sin egen lastetilstand.
 */
const partitionApplications = (
  applications: ApplicationListItemType[] | null,
) => {
  if (!applications) {
    return { drafts: null, active: null, archived: null };
  }

  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

  // Dato saken ble avgjort: bevilgningens innsendingsdato når den finnes,
  // ellers søknadens egen arkiveringsdato.
  const handledDate = (a: ApplicationListItemType) =>
    a.award && a.award.submittedon ? a.award.submittedon : a.archivedon;

  return {
    drafts: applications.filter((a) => a.status === 9),

    active: applications.filter((a) => {
      if (a.status === 9) return false;
      const awardStatus = a.award?.status;
      const date = handledDate(a);

      // include always when award missing or award.status === 10
      if (awardStatus == null || awardStatus === 10 || awardStatus == 90)
        return true;

      // otherwise include only if not archived or archived within last month
      return date === null || new Date(date) > oneMonthAgo;
    }),

    archived: applications.filter((a) => {
      if (a.status === 9) return false;
      const awardStatus = a.award?.status;
      const date = handledDate(a);

      // If status is 20 or 30, treat as archived
      if (awardStatus == 20 || awardStatus == 30) {
        if (date !== null && new Date(date) < oneMonthAgo) {
          return true;
        }
      }

      return false;
    }),
  };
};

const Provider = ({ children }: { children: ReactNode }) => {
  const { apiCall, user, sub } = useAuthContext();
  const { data: entityData } = useConfigContext();

  //Email preferences check for each entity
  const getInitialPreference = () => {
    const entityId = entityData?.id;
    const perEntity = user?.properties?.email_preferences;
    if (perEntity && entityId && String(entityId) in perEntity) {
      return perEntity[String(entityId)];
    }
    return false;
  };

  const [applications, setApplications] = useState<
    ApplicationListItemType[] | null
  >(null);
  const [messages, setMessages] = useState<MessageType[] | null>(null);
  const [reports, setReports] = useState<ReportItemType[] | null>(null);
  const [agreements, setAgreements] = useState<AgreementType[] | null>(null);
  const [loadFailed, setLoadFailed] = useState<boolean>(false);
  const [preference, setPreference] = useState<boolean>(getInitialPreference());

  const { drafts, active, archived } = useMemo(
    () => partitionApplications(applications),
    [applications],
  );

  // Tre uavhengige kall. Fristene og de uleste meldingene er ikke avhengige av
  // søknadslista, så de skal ikke vente på den — hver kolonne fyller seg så
  // snart dens eget kall er tilbake.
  //
  // `sub` kommer fra AuthProvider; readOidcSub() er samme fallback
  // NewApplicationProvider bruker når OIDC-konteksten ikke er klar ennå.
  const authSub = sub || readOidcSub();

  useEffect(() => {
    if (!authSub) return;
    let cancelled = false;
    fetchApplications(authSub).then((rows) => {
      if (cancelled) return;
      if (rows) setApplications(rows);
      else setLoadFailed(true);
    });
    return () => {
      cancelled = true;
    };
  }, [authSub]);

  useEffect(() => {
    if (!authSub) return;
    let cancelled = false;
    fetchDeadlines(authSub).then((data) => {
      if (cancelled) return;
      if (!data) {
        setLoadFailed(true);
        return;
      }
      setReports(data.reports);
      setAgreements(data.agreements);
    });
    return () => {
      cancelled = true;
    };
  }, [authSub]);

  useEffect(() => {
    if (!authSub) return;
    let cancelled = false;
    fetchUnreadMessages(authSub).then((rows) => {
      if (cancelled) return;
      if (rows) setMessages(rows);
      else setLoadFailed(true);
    });
    return () => {
      cancelled = true;
    };
  }, [authSub]);

  return (
    <Context.Provider
      value={{
        active,
        archived,
        messages,
        drafts,
        reports,
        agreements,
        loadFailed,
        preference,
        deleteDraft: async (draftId: number, applicationType: number) => {
          const response = await apiCall({
            action: "deleteApplicationDraftFromDB",
            draftId,
            applicationType,
          });

          if (response?.data?.status === 200) {
            // Kladdene utledes fra søknadslista, så vi fjerner raden der.
            setApplications((prev) =>
              prev ? prev.filter((a) => a.id !== draftId) : prev,
            );
          }
        },
        handlePreferenceChange: async (value: boolean) => {
          setPreference(value);

          try {
            const response = await apiCall({
              action: "updateEmailPreference",
              value,
            });

            if (!response?.data?.success) {
            }
          } catch (error) {
            console.error(error);
          }
        },
      }}
    >
      {children}
    </Context.Provider>
  );
};

const Dashboard = () => {
  const { logout } = useAuthContext();
  const { t } = useTranslation();
  const playgroundAccess = usePlaygroundAccess();

  return (
    <Provider>
      <div className="container min-vh-100 d-flex flex-column">
        <Header />
        <div className="row mb-lg-4 mt-4 mt-lg-0">
          <div className="col">
            <h1 className="display-1">{t("dashboard_title")}</h1>
          </div>
        </div>
        <div className="tabs row">
          <div className="col">
            <ul className="nav mb-4 mb-lg-5 position-relative border-bottom border-4">
              <CustomLink to="">{t("dashboard_your_overview")}</CustomLink>
              <CustomLink to="arkiv">{t("dashboard_archive")}</CustomLink>
              <CustomLink to="rediger-profil">
                {t("dashboard_edit_profile")}
              </CustomLink>
              {playgroundAccess && (
                <CustomLink to="lekeplass">{t("dashboard_playground")}</CustomLink>
              )}

              <li className="nav-item text-end" style={{ flex: 1 }}>
                <a
                  className="nav-link d-inline-block px-0"
                  href="#"
                  onClick={logout}
                >
                  {t("logout")}
                  <FontAwesomeIcon
                    icon={["fal", "arrow-right-from-bracket"]}
                    className="ms-2"
                  />
                </a>
              </li>
            </ul>
          </div>
        </div>

        <Outlet />
        <Footer />
      </div>
    </Provider>
  );
};

export const Overview = () => {
  const { activeReportStatuses, applications, data, disableIllustrations } =
    useConfigContext();
  const {
    active,
    drafts,
    messages,
    reports,
    agreements,
    loadFailed,
    preference,
    handlePreferenceChange,
  } = useDashboardContext();
  const [modalActive, setModalActive] = useState<boolean>(false);
  const { t } = useTranslation();
  const { user, apiCall, setUserData } = useAuthContext();
  const [hidePreferenceBox, setHidePreferenceBox] = useState<string | null>(
    sessionStorage.getItem("lyfti_email_preference_hide"),
  );

  const closeInfoBox = async () => {
    const response = await apiCall({
      action: "updateUserProperties",
      properties: {
        hide_infobox: true,
      },
    });

    if (response?.data?.status === 200 && user) {
      setUserData({
        ...user,
        properties: {
          ...user.properties,
          hide_infobox: true,
        },
      });
    }
  };

  const closePreferenceBox = () => {
    setHidePreferenceBox("true");
    sessionStorage.setItem("lyfti_email_preference_hide", "true");
  };

  return (
    <>
      {loadFailed && (
        <div className="row mb-2">
          <div className="col-12">
            <AlertBox classes="w-100" background="danger">
              <h6 className="mb-0">{t("dashboard_load_failed")}</h6>
            </AlertBox>
          </div>
        </div>
      )}
      {preference != true &&
        !hidePreferenceBox &&
        useConfigContext().cmEnabled && (
          <div className="row mb-2">
            <div className="col-12">
              <AlertBox classes="w-100" background="primary">
                <div className="d-flex justify-content-between">
                  <h6>{parse(t("dashboard_email_preference"))}</h6>
                  <a
                    href="#"
                    onClick={closePreferenceBox}
                    className="text-decoration-none text-secondary"
                  >
                    <FontAwesomeIcon
                      icon={["fal", "xmark"]}
                      size="xl"
                      className="ms-5 text-body"
                    />
                  </a>
                </div>

                <ToggleSwitch
                  label="Ja, jeg samtykker"
                  value={preference}
                  onCheck={handlePreferenceChange}
                  description=""
                />
              </AlertBox>
            </div>
          </div>
        )}
      <div className="row">
        <div className="col-12 col-lg-7 mb-1">
          <h4>{t("dashboard_your_applications")}</h4>
          <p>{t("dashboard_your_applications_text")}</p>
          <div className="mb-4">
            <Button
              title={t("dashboard_new_application")}
              type="primary"
              rightIcon="arrow-right"
              onClick={() => {
                setModalActive(!modalActive);
              }}
            />

            {/* Cant find application infobox */}
            {user?.properties.hide_infobox !== true ? (
              <div className="mt-3">
                <AlertBox classes="w-100" background="primary">
                  <div className="d-flex justify-content-between">
                    <h6>{t("dashboard_cant_find_application")}</h6>
                    <a
                      href="#"
                      onClick={closeInfoBox}
                      className="text-decoration-none text-secondary"
                    >
                      {t("dashboard_cant_find_application_read")}
                      <FontAwesomeIcon
                        icon={["fal", "xmark"]}
                        className="ms-2"
                      />
                    </a>
                  </div>

                  <p>
                    {parse(
                      t("dashboard_cant_find_application_text", {
                        website_contact_url: data?.url_webpage_contact,
                      }),
                    )}
                  </p>
                </AlertBox>
              </div>
            ) : null}
          </div>

          {/* Applications */}
          <Skeleton
            name="application-draft"
            loading={!drafts}
            color="#e0e0e0"
          >
          {drafts && (
            drafts.map((item: ApplicationListItemType, index: Key) => (
              <ApplicationDraft key={index} {...item} />
            ))
          )}
          </Skeleton>

          {active ? (
            [...active]
              .sort(
                (a, b) =>
                  new Date(b.archivedon ?? b.createdon).getTime() -
                  new Date(a.archivedon ?? a.createdon).getTime(),
              )
              .map((item: ApplicationListItemType, index: Key) => (
                <ApplicationItem key={index} {...item} />
              ))
          ) : (
            <p>{t("loading")}</p>
          )}
        </div>
        <div className="col-12 col-lg-5">
          {/* Reports */}
          <h4 className="mb-3">{t("dashboard_due")}</h4>
          {reports &&
          Object.values(
            reports
              // `/rest/application/deadlines` filtrerer allerede serverside:
              // «Forespurt» og revisjonskladder, alle med frist. Kladder kan
              // altså ikke lenger utelukkes på status her — revisjonskladden
              // ER en status 9 som skal stå i fristkolonnen.
              .filter(
                (item) =>
                  activeReportStatuses.includes(item.status) &&
                  item.expireson,
              )
              .reduce(
                (acc, item) => {
                  const existing = acc[item.applicationId];
                  if (
                    !existing ||
                    new Date(item.expireson).getTime() <
                      new Date(existing.expireson).getTime()
                  ) {
                    acc[item.applicationId] = item;
                  }
                  return acc;
                },
                {} as Record<number, ReportItemType>,
              ),
          )
            .sort(
              (a, b) =>
                new Date(a.expireson).getTime() -
                new Date(b.expireson).getTime(),
            )
            .map((item, index) => <ReportItem key={index} {...item} />)}

          {/* Agreements — `/rest/application/deadlines` returnerer kun avtaler
              som venter på DENNE brukerens signatur (ikke manuelt signert,
              ingen agreement_signing fra brukeren), så det er ingen
              filtrering å gjøre her. */}
          {agreements?.length
            ? [...agreements]
                .sort(
                  (a, b) =>
                    new Date(a.deadline).getTime() -
                    new Date(b.deadline).getTime(),
                )
                .map((item, key) => <AgreementItem key={key} {...item} />)
            : null}


          {/* Messages */}
          <h4 className="mb-3 mt-4 mt-lg-5">
            {t("dashboard_unread_messages")}
          </h4>
          {!messages ? (
            <div className="row justify-content-center py-5">
              <FontAwesomeIcon icon={["fad", "spinner-third"]} spin size="3x" />
            </div>
          ) : messages.length ? (
            messages.map((item, key) => <MessageItem key={key} {...item} />)
          ) : (
            <div>
              <p>
                <i>{t("dashboard_no_unread_messages")}</i>
              </p>
            </div>
          )}
        </div>

        <BubbleModal active={modalActive} setActive={setModalActive}>
          {!disableIllustrations && (
            <img
              className="custom-modal-image mb-5"
              src={
                import.meta.env.VITE_API_BASE_URL + "assets/svg/soknad-gfx.png"
              }
              alt={t("choose_application_type")}
            />
          )}
          <h2 className="mb-4">{t("dashboard_what_apply_title")}</h2>

          <div className="w-100">
            {applications &&
              applications
                // `published` er eneste kilde til synlighet for søkere. Her sto
                // `item.public !== false`, som bare fantes i portal_config og
                // ikke kunne styres fra Innstillinger — synlighetsbryteren i
                // admin hadde derfor ingen effekt. Se
                // migrate_public_to_published.php.
                .filter((item: ApplicationType) => item.published !== false)
                .map((item: ApplicationType, key: Key) => {
                  return (
                    <ApplicationChoice
                      key={key}
                      item={item}
                      setModalActive={setModalActive}
                    />
                  );
                })}
          </div>

          <a
            className="card-link text-decoration-none return-link"
            href="#"
            onClick={() => setModalActive(false)}
          >
            <FontAwesomeIcon icon={["fal", "arrow-left"]} className="me-1" />
            {t("back")}
          </a>
        </BubbleModal>
      </div>
    </>
  );
};

export const Archive = () => {
  const { archived } = useDashboardContext();
  const { t } = useTranslation();

  return (
    <div className="row">
      <div className="col-12 mb-1">
        <h4>{t("dashboard_archive")}</h4>
        <p>{t("dashboard_archive_text")}</p>

        {archived
          ? [...archived]
              .sort(
                (a, b) =>
                  new Date(b.archivedon ?? 0).getTime() -
                  new Date(a.archivedon ?? 0).getTime(),
              )
              .map((item, index) => <ApplicationItem key={index} {...item} />)
          : null}
      </div>
    </div>
  );
};

export const Profile = () => {
  const { t } = useTranslation();
  const { preference, handlePreferenceChange } = useDashboardContext();
  const { disableIllustrations } = useConfigContext();
  const {
    user,
    saveUserProfile,
    setUserData,
    subscribeEarlyAccess,
    earlyAccess,
  } = useAuthContext();
  const [formData, setFormData] = useState<FormDataType>(user || {});
  const [errors, setErrors] = useState<FormDataType>({});
  const [changed, setChanged] = useState<boolean>(false);
  const [isValidated, setValidated] = useState<Record<string, boolean>>({
    fullname: false,
    email: false,
    phone: false,
    address: false,
    city: false,
    zip: false,
  });
  const [isSending, setSending] = useState<boolean>(false);
  const UserSchema = getUserSchema(t);

  const handleInputChange = async (
    name: keyof typeof UserSchema.shape,
    value: string,
  ) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setValidated((prev) => ({ ...prev, [name]: true }));

    const InputSchema = UserSchema.shape[name];
    const valid = InputSchema.safeParse(value);
    if (valid.success) {
      setErrors((prev) => {
        const { [name]: _, ...rest } = prev;
        return rest;
      });
    } else {
      setErrors((prev) => ({
        ...prev,
        [name]: valid.error.errors[0].message,
      }));
    }

    if (name === "zip") {
      if (value.length === 4) {
        const response = await getCityFromPostCode(value);
        setFormData((prevData) => ({
          ...prevData,
          city: response,
        }));
      } else {
        setFormData((prevData) => ({
          ...prevData,
          city: "",
        }));
      }
    }
  };

  const validateForm = async () => {
    setSending(true);

    try {
      const valid = UserSchema.safeParse(formData);
      if (valid.success) {
        const response = await saveUserProfile(formData);
        if (response?.data?.status === 200) {
          setUserData(formData as UserType);
          setErrors({});
          setChanged(true);
          setValidated({
            fullname: false,
            email: false,
            phone: false,
            address: false,
            city: false,
            zip: false,
          });
        }
        if (response?.data?.status === 400) {
          setErrors((prev) => ({
            ...prev,
            [response.data.field]: t(response.data.lingo),
          }));
          setChanged(false);
        }
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <h4>{t("dashboard_edit_profile")}</h4>

      {changed && (
        <div className="row mt-2">
          <div className="col">
            <AlertBox background="success">
              {t("profile_changed_saved")}
            </AlertBox>
          </div>
        </div>
      )}

      <div className="row">
        <div className={"col-12 col-lg-8"}>
          <div className="row mt-1 gy-3 gy-lg-4">
            <div className="col-lg-6">
              <Input
                value={formData["fullname"]}
                name="fullname"
                type="text"
                label={t("input_fullname_label")}
                placeholder={t("input_fullname_placeholder")}
                error={errors["fullname"]}
                startIcon="user"
                disabled
                showValidation={isValidated["fullname"]}
                required
                rules={{}}
                maxLength={25}
              />
            </div>
            <div className="col-lg-6">
              <Input
                value={formData["email"]}
                onChange={(value) => handleInputChange("email", value)}
                name="email"
                type="text"
                label={t("input_email_label")}
                placeholder={t("input_email_placeholder")}
                error={errors["email"]}
                startIcon="at"
                showValidation={isValidated["email"]}
                required
                rules={{}}
                maxLength={50}
              />
            </div>
            <div className="col-lg-5">
              <Input
                value={formData["phone"]}
                onChange={(value) => handleInputChange("phone", value)}
                name="phone"
                type="text"
                label={t("input_phone_label")}
                placeholder={t("input_phone_placeholder")}
                error={errors["phone"]}
                startIcon="phone"
                showValidation={isValidated["phone"]}
                required
                rules={{}}
                maxLength={25}
              />
            </div>
            <div className="col-lg-7">
              <Input
                value={formData["address"]}
                onChange={(value) => handleInputChange("address", value)}
                name="address"
                type="text"
                label={t("input_address_label")}
                placeholder={t("input_address_placeholder")}
                error={errors["address"]}
                startIcon="house"
                showValidation={isValidated["address"]}
                required
                rules={{}}
                maxLength={50}
              />
            </div>
            <div className="col-lg-5">
              <Input
                value={formData["zip"]}
                onChange={(value) => handleInputChange("zip", value)}
                name="zip"
                type="text"
                label={t("input_zip_label")}
                placeholder={t("input_zip_label")}
                error={errors["zip"]}
                startIcon="signs-post"
                showValidation={isValidated["zip"]}
                required
                rules={{}}
                maxLength={50}
              />
            </div>
            <div className="col-lg-7">
              <Input
                value={formData["city"]}
                name="city"
                type="text"
                label={t("input_city_label")}
                placeholder={t("input_city_label")}
                error={errors["city"]}
                startIcon="building"
                disabled
                showValidation={isValidated["city"]}
                required
                rules={{}}
                maxLength={50}
              />
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          {useConfigContext().cmEnabled && (
            <div className="p-4 border border-1 rounded-4 mb-4">
              <h4>Oppfølging på e-post</h4>
              <p>
                Ønsker du å motta inspirasjon, nyttige tips og råd og varsler om
                gavekampanjer via e-post? Du kan når som helst enkelt melde deg
                av ved en senere anledning.
              </p>

              <ToggleSwitch
                label="Ja, jeg samtykker"
                value={preference}
                onCheck={handlePreferenceChange}
                description=""
              />
            </div>
          )}
          <div className="p-4 border border-1 rounded-4">
            <h4>Tidlig tilgang</h4>
            <p>
              Ønsker du å prøve ut ny funksjonalitet tidlig? Meld deg på tidlig
              tilgang, men vær obs på at det kan være feil og mangler.
            </p>

            <ToggleSwitch
              label="Meld på"
              value={earlyAccess}
              onCheck={subscribeEarlyAccess}
              description=""
            />
          </div>
        </div>
      </div>

      <div className="row mt-4">
        <div className="col">
          <Button
            title={t("save")}
            onClick={validateForm}
            rightIcon="floppy-disk"
            disabled={
              isSending || Object.entries(errors).length > 0 || user == formData
            }
          />
        </div>
      </div>

      <div className="row mt-5 align-items-center">
        {!disableIllustrations && (
          <div className="col-auto mb-4">
            <img
              src={import.meta.env.VITE_API_BASE_URL + "assets/svg/tips.svg"}
              alt=""
            />
          </div>
        )}
        <div className="col">
          <h4>{t("edit_profile_locked_fields")}</h4>
          <p>{t("edit_profile_locked_fields_text")}</p>
        </div>
      </div>
    </>
  );
};

const ApplicationItem = (props: ApplicationListItemType) => {
  const {
    id,
    name,
    status,
    state,
    applicant_name,
    type_name,
    archivedon,
    createdon,
    award,
  } = props;
  const { t } = useTranslation();
  const statusObject = getStatus(status, t);
  const navigate = useNavigate();

  const isUnhandled = state !== 90;

  const handleClick = (e: MouseEvent) => {
    e.preventDefault();
    navigate(`/soknad/${id}`);
  };

  const parseDate = (value: any): Date | null => {
    if (!value) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  };

  const createdDate = parseDate(createdon);
  const archivedDate = parseDate(archivedon);
  const submittedDate = parseDate(award?.submittedon);

  const handledDate = submittedDate || archivedDate;

  const dateToFormat = isUnhandled ? createdDate : handledDate;
  const dateString = dateToFormat
    ? `${format(dateToFormat, "dd.MM.yyyy")} kl. ${format(dateToFormat, "HH:mm")}`
    : t("unknown_date");

  return (
    <a
      href="#"
      className="card mb-4 border text-decoration-none state-unprocessed"
      onClick={handleClick}
    >
      <div className="card-body">
        <div className="row">
          <div className="col">
            <h5 className="card-title mb-1">{name ? name : "Uten navn"}</h5>
            <div className="d-none d-lg-inline-block">
              <p className="card-text text-muted">
                <span className="fw-medium">#{id} </span>
                {applicant_name}. {type_name}.{" "}
                {!isUnhandled ? statusObject?.base_label + " gitt" : "Sendt"}{" "}
                {dateString}
              </p>
            </div>
          </div>
          <div className="col-auto">
            <span
              className={`fw-medium text-nowrap ${!isUnhandled ? statusObject?.className : "text-secondary"}`}
            >
              {isUnhandled ? t("status_unhandled") : statusObject?.label}
              <FontAwesomeIcon
                className="ms-2"
                icon={
                  isUnhandled
                    ? ["far", "circle-minus"]
                    : ["far", statusObject?.icon as IconName]
                }
              />
            </span>
            <span className="text-decoration-none card-link mt-1 d-none d-lg-block text-end">
              {t("open")}
              <FontAwesomeIcon icon={["far", "arrow-right"]} className="ms-1" />
            </span>
          </div>
        </div>

        <div className="row d-lg-none">
          <p className="card-text text-muted">
            <span className="fw-medium">#{id} </span>
            {applicant_name}. {type_name}.{" "}
            {!isUnhandled ? statusObject?.base_label + " gitt" : "Sendt"}{" "}
            {dateString}
          </p>
        </div>
      </div>
    </a>
  );
};

const ApplicationDraft = (props: ApplicationListItemType) => {
  const { type, type_name, editedon, status, id, createdon, name, applicant_name } = props;
  const { t } = useTranslation();
  const { applications, setSkipDraftChoice, setDraftId } = useConfigContext();
  const { deleteDraft } = useDashboardContext();
  const statusObject = getStatus(status, t);
  const [url, setUrl] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const navigate = useNavigate();
  const [appConfig, setAppCofig] = useState<ApplicationType>(null)
  const {user} = useAuthContext()

  const resolvedPeriod = appConfig? getActiveOrNextPeriod(appConfig.periods, new Date()) : null;
  const isWithin = resolvedPeriod?.isWithin ?? false;
  // const shouldDisable = true
  const shouldDisable = appConfig != null && !isWithin && !appConfig.periods_text?.search_outside_periods && !user?.admin;

  const handleClick = () => {
    setDraftId(id);
    setSkipDraftChoice(true);
    navigate(`/soknadskjema/${url}`);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmDelete(true);
  };

  useEffect(() => {
    if (applications) {
      const applicationType = applications.filter(
        (a: ApplicationType) => a.type === type,
      );
      if (applicationType.length) {
        setUrl(applicationType[0].url);
        setAppCofig(applicationType[0])
      }
    }
  }, [applications]);
  if (url === null) return <div></div>;

  const updatedText =
    [type_name, applicant_name].filter(Boolean).join(" · ") +
    `. ${t("last_updated")} ` +
    format(editedon ? editedon : createdon, "dd.MM.yyy") +
    " kl. " +
    format(editedon ? editedon : createdon, "HH:mm");

  return (
    <>
      {confirmDelete && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050, backdropFilter: "blur(0.7px)" }}
          onClick={() => setConfirmDelete(false)}
        >
          <div
            className="bg-white rounded-4 p-4"
            style={{ maxWidth: 400, width: "90%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="mb-3 fw-bold">{t("delete_draft_title")}</h4>
            <p className="mb-4">{t("delete_draft_confirm")}</p>
            <div className="d-flex gap-3">
              <button
                className="btn btn-danger btn-hover-scale"
                onClick={() => {
                  deleteDraft(id, type);
                  clearActiveDraft(url, id);
                  setConfirmDelete(false);
                }}
              >
                {t("delete_draft_yes")}
              </button>
              <button
                className="btn btn-outline-primary"
                onClick={() => setConfirmDelete(false)}
              >
                {t("delete_draft_no")}
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className={`card mb-4 border text-decoration-none state-draft ${shouldDisable ? "" : "cursor-pointer "}`}
        onClick={shouldDisable ? undefined : handleClick}
      >
        <div className="card-body">
          <div className="row">
            <div className="col">
              <h5 className="card-title mb-1">
                {name || t("application")}
                <span className="text-muted fw-normal ms-2 small">#{id}</span>
              </h5>
              <div className="d-none d-lg-inline-block">
                <p className="card-text text-body">{updatedText}</p>
              </div>
            </div>
            <div className="col-auto d-flex flex-column align-items-end">
              <span
                className={`fw-medium text-nowrap ${statusObject?.className}`}
              >
                {statusObject?.label}
                {!shouldDisable && (
                   <FontAwesomeIcon
                  className="ms-2"
                  icon={["far", statusObject?.icon as IconName]}
                />
                )}
               
              </span>
              <div className="d-flex gap-3">
                <span
                  className="text-decoration-none card-link mt-1 d-none d-lg-block text-end cursor-pointer"
                  onClick={handleDelete}
                >
                  {t("delete")}
                  <FontAwesomeIcon icon={["far", "xmark"]} className="ms-1" />
                </span>
                {!shouldDisable ? (
                  <span className="text-decoration-none card-link mt-1 d-none d-lg-block text-end">
                  {t("continue")}
                  <FontAwesomeIcon
                    icon={["far", "arrow-right"]}
                    className="ms-1"
                  />
                </span>
                ) : (
                  <span className="text-decoration-none mt-1 d-none d-lg-block text-end text-black">
                    {t("application_period_closed")}
                  </span>
                )}
                
              </div>
            </div>
          </div>

          <div className="row d-lg-none">
            <p className="card-text text-body">{updatedText}</p>
          </div>
        </div>
      </div>
    </>
  );
};

const MessageItem = (props: MessageType) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { message, sent, application, from_name, from_photo } = props;

  const updateImgSrc = (htmlString: string) => {
    return htmlString.replace(/<img\s+src="([^"]+)"/g, (_, src) => {
      const newSrc = src.startsWith("http")
        ? src
        : `${import.meta.env.VITE_ADMIN_API_URL}${src}`;
      return `<img src="${newSrc}"`;
    });
  };

  return (
    <a
      href="#"
      className="d-flex align-items-start card-message text-decoration-none state-message-unread mb-4"
      onClick={() => navigate(`/soknad/${application}/meldinger`)}
    >
      {/* <img src="/svg/icon-author.jpg" className="rounded-circle me-2" style={{ maxWidth: '32px' }} alt="User Avatar" /> */}
      <ProfileAvatar name={from_name} photo={from_photo} />
      <div className="w-100 ms-2">
        <div className="d-flex justify-content-between mb-1">
          <small className="text-muted">
            <strong>#{application}</strong>{" "}
            {formatDistanceToNow(sent, { addSuffix: true, locale: nb })} av{" "}
            {from_name}
          </small>
          <small>
            <span className="card-link d-none d-lg-block">
              {t("go_to_conversation")}{" "}
              <FontAwesomeIcon icon={["fal", "arrow-right"]} className="ms-1" />
            </span>
          </small>
        </div>
        <div className="card card-message-comment">
          <div className="card-body">
            <div className="card-text">{parse(updateImgSrc(message))}</div>
          </div>
        </div>
      </div>
    </a>
  );
};

const ApplicationChoice = ({
  item,
  setModalActive,
}: {
  item: ApplicationType;
  setModalActive: (value: boolean) => void;
}) => {
  const navigate = useNavigate();
  const { setSkipDraftChoice, setDraftId } = useConfigContext();
  const { user } = useAuthContext();
  const resolvedPeriod = getActiveOrNextPeriod(item.periods, new Date());
  const isWithin = resolvedPeriod?.isWithin ?? false;
  const template = isWithin
    ? item.periods_text?.inside
    : item.periods_text?.outside;
  const periodText = formatPeriodText(template, resolvedPeriod);

  // Only disable if not within period AND search_outside_periods is false
  const shouldDisable =
    !isWithin && !item?.periods_text?.search_outside_periods && !user?.admin;

  const goToApplication = () => {
    setDraftId(null);
    clearActiveDraft(item.url);
    setSkipDraftChoice(false);
    setModalActive(false);
    navigate("/soknadskjema/" + item.url);
  };

  return (
    <a
      // Deaktivert kort: uten href er lenka ikke klikkbar eller fokuserbar.
      // Elementtypen beholdes så lenkefargen fra stylesheetet er uendret.
      href={shouldDisable ? undefined : "#"}
      aria-disabled={shouldDisable || undefined}
      className={`card mb-3 border text-decoration-none w-100 next-gift-card w-100 ${shouldDisable ? "deactivated" : ""}`}
      style={shouldDisable ? { cursor: "not-allowed" } : undefined}
      onClick={shouldDisable ? undefined : () => goToApplication()}
    >
      <div className="card-body d-flex justify-content-between align-items-center">
        <div>
          <h5 className="card-title mb-1">
            {item.title}
            {!isWithin &&
            !item?.periods_text?.search_outside_periods &&
            user?.admin
              ? " (stengt for søkere)"
              : ""}
          </h5>
          <p
            className={`card-text ${shouldDisable ? "text-muted" : "application-choice-text"}`}
          >
            {periodText}
          </p>
        </div>
        <div className="text-end d-flex flex-column ms-2">
          <span className="mt-1">
            <FontAwesomeIcon
              icon={["fal", "arrow-right"]}
              style={{ fontSize: 30 }}
            />
          </span>
        </div>
      </div>
    </a>
  );
};

export default Dashboard;